import { apiRequest, investmentEntityRequest } from '@utils/request';
import { Asset, PaginatedAssets } from 'types/assets';
import { AssetQueryParams } from 'types/sanity/proxy';
import { serializeQueryParams } from '@utils/sanity/proxy-query-params-serializer';
import { useAppStore } from '@stores/app';

/**
 * Fetches assets for the Venture marketing page based on their order in Sanity Studio.
 * This endpoint leverages the same controller on the API side as getAssets below, but doesn't
 * support pagination at this time.
 */
export async function getMarketingPageVentureAssets(): Promise<Array<Asset>> {
	const response = await apiRequest.get('/marketing-page/venture');

	return response.data.assets;
}

/**
 * Fetches assets based on the provided query parameters.
 *
 * @param {AssetQueryParams} [params] - Optional query parameters to filter and customize the asset retrieval.
 * @param {boolean} [forceAuthenticatedRequest=false] - Flag indicating whether to bypass the advisor account check and force the use of the authenticated investmentEntity endpoint.
 *                                              		If `true`, the function will call the `investmentEntity` endpoint, even if the user is an advisor. This should only be set to `true`
 *                                              		for instances when the advisor account has an associated investmentEntity.
 *
 * All parameters are optional, and the request returns all assets if no parameters are supplied.
 *
 * By supplying the correct `include` query param, you can retrieve
 * information that is omitted by default.
 *
 * For more information, see the API overview documentation:
 * [Sanity Asset REIT migration - API overview](https://fundrise.atlassian.net/wiki/spaces/P/pages/3431038986/Sanity+Asset+REIT+migration+-+API+overview)
 * [API Query Parameter Overview](https://fundrise.atlassian.net/wiki/spaces/P/pages/3655925776/API+Query+Parameter+Overview#Asset-Query-Params)
 */
export async function getAssets(
	params?: AssetQueryParams,
	forceAuthenticatedRequest = false
): Promise<PaginatedAssets> {
	let response;

	const appStore = useAppStore();
	const payload = {
		params,
		paramsSerializer: serializeQueryParams
	};

	if ((appStore.isAuthenticated && !appStore.isAdvisor) || forceAuthenticatedRequest) {
		response = await investmentEntityRequest.get('/assets', payload);
	} else {
		response = await apiRequest.get('/assets', payload);
	}

	return response.data;
}

/**
 * Get a single asset by ID
 */
export async function getAsset(id: string, params?: Omit<AssetQueryParams, 'fundriseId'>): Promise<Asset | null> {
	if (!params?.assetClass && !params?.elementType) {
		throw new Error('getAsset: At least one of "assetClass" or "elementType" must be provided.');
	}

	const assets = await getAssets({
		...params,
		fundriseId: id,
		pageSize: 1,
		pageNumber: 1
	});

	return assets.results[0] ?? null;
}
